import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth, API } from "@/App";
import axios from "axios";
import { 
  Fish, 
  Users, 
  CreditCard, 
  Award, 
  Store, 
  Camera, 
  MessageCircle,
  ChevronRight,
  Check,
  Star
} from "lucide-react";

const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [memberOfMonth, setMemberOfMonth] = useState(null);
  const [retailers, setRetailers] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [momRes, retailersRes] = await Promise.all([
          axios.get(`${API}/member-of-month`),
          axios.get(`${API}/retailers`)
        ]);
        setMemberOfMonth(momRes.data);
        setRetailers(retailersRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
    // Seed data on first load
    axios.post(`${API}/seed`).catch(() => {});
  }, []);

  const benefits = [
    { icon: CreditCard, title: "Digital Membership Card", description: "Access your unique eCard anytime to get discounts" },
    { icon: Store, title: "10% Off at Partners", description: "Exclusive discounts at leading tackle shops" },
    { icon: Camera, title: "Log Your Catches", description: "Track and share your fishing achievements" },
    { icon: Users, title: "Join the Community", description: "Connect with fellow fishing enthusiasts" },
    { icon: Award, title: "Win Monthly Prizes", description: "Feature as Angler of the Month" },
    { icon: MessageCircle, title: "Expert Q&A", description: "Get your fishing questions answered" },
  ];

  const fishingTypes = ["Fresh Water", "Specimen", "Bass", "Fly Fishing", "Rock and Surf", "Boat", "Offshore", "Estuary"];

  return (
    <div className="min-h-screen bg-[var(--bg-main)]">
      {/* Hero Section */}
      <section 
        className="hero-section min-h-[90vh] flex items-center justify-center relative"
        style={{ backgroundImage: `url(https://images.unsplash.com/photo-1720631269479-3a20ac2f06f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxmbHklMjBmaXNoaW5nJTIwcml2ZXIlMjBzdW5zZXR8ZW58MHx8fHwxNzY5NDg5NTIyfDA&ixlib=rb-4.1.0&q=85)` }}
      >
        {/* Navigation */}
        <nav className="absolute top-0 left-0 right-0 z-20 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/logo.jpg" alt="FishingFanatix" className="h-12 w-auto" />
            </div>
            <div className="flex items-center gap-4">
              {user ? (
                <Button 
                  onClick={() => navigate("/dashboard")}
                  className="btn-accent"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    className="text-white hover:text-[var(--sunrise-orange)]"
                    onClick={() => navigate("/login")}
                    data-testid="login-btn"
                  >
                    Login
                  </Button>
                  <Button 
                    onClick={() => navigate("/register")}
                    className="btn-accent"
                    data-testid="join-now-btn"
                  >
                    Join Now
                  </Button>
                </>
              )}
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Badge className="mb-6 bg-[var(--sunrise-orange)] text-[var(--deep-ocean)] px-4 py-2 text-sm font-semibold">
              South Africa's Premier Fishing Community
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight">
              Your Fishing Journey<br />
              <span className="text-[var(--sunrise-orange)]">Starts Here</span>
            </h1>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              Join thousands of passionate anglers. Get exclusive discounts, log your catches, 
              and connect with the FishingFanatix community.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate("/register")}
                className="btn-accent text-lg px-10 py-6"
                data-testid="get-started-btn"
              >
                Get Started <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                variant="outline"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full text-lg px-10 py-6"
                onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                data-testid="view-pricing-btn"
              >
                View Pricing
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[var(--deep-ocean)] mb-4">
              Member Benefits
            </h2>
            <p className="text-[var(--river-stone)] text-lg max-w-2xl mx-auto">
              Everything you need to enhance your fishing experience
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-white card-shadow hover:card-shadow-deep transition-all duration-300 border-0">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-2xl bg-[var(--deep-ocean)]/10 flex items-center justify-center mb-6">
                      <benefit.icon className="w-7 h-7 text-[var(--deep-ocean)]" />
                    </div>
                    <h3 className="text-xl font-bold text-[var(--deep-ocean)] mb-3">{benefit.title}</h3>
                    <p className="text-[var(--river-stone)]">{benefit.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Fishing Types Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[var(--deep-ocean)] mb-4">
              For Every Type of Angler
            </h2>
            <p className="text-[var(--river-stone)] text-lg">
              Whether you're into freshwater or offshore, we've got you covered
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4">
            {fishingTypes.map((type, index) => (
              <motion.div
                key={type}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Badge 
                  variant="outline" 
                  className="px-6 py-3 text-base border-2 border-[var(--deep-ocean)]/20 text-[var(--deep-ocean)] hover:bg-[var(--deep-ocean)] hover:text-white transition-all cursor-default"
                >
                  {type}
                </Badge>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[var(--deep-ocean)] mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-[var(--river-stone)] text-lg">
              Choose the plan that works best for you
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Monthly Plan */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-white card-shadow-deep border-0 h-full">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-[var(--deep-ocean)] mb-2">Monthly</h3>
                  <p className="text-[var(--river-stone)] mb-6">Perfect for trying us out</p>
                  <div className="mb-6">
                    <span className="text-5xl font-extrabold text-[var(--deep-ocean)]">R75</span>
                    <span className="text-[var(--river-stone)]">/month</span>
                  </div>
                  <ul className="space-y-4 mb-8">
                    {["Digital Membership Card", "10% Discount at Partners", "Catch Logging", "Community Access", "Q&A Support"].map((feature) => (
                      <li key={feature} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-[var(--success)]" />
                        <span className="text-[var(--river-stone)]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={() => navigate("/register")}
                    className="w-full btn-secondary"
                    data-testid="monthly-plan-btn"
                  >
                    Start Monthly
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Annual Plan */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-[var(--deep-ocean)] card-shadow-deep border-0 h-full relative overflow-hidden">
                <div className="absolute top-4 right-4">
                  <Badge className="bg-[var(--sunrise-orange)] text-[var(--deep-ocean)] font-bold">
                    Save R400
                  </Badge>
                </div>
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Annual</h3>
                  <p className="text-white/70 mb-6">Best value for committed anglers</p>
                  <div className="mb-6">
                    <span className="text-5xl font-extrabold text-white">R500</span>
                    <span className="text-white/70">/year</span>
                  </div>
                  <ul className="space-y-4 mb-8">
                    {["Everything in Monthly", "2 Months FREE", "Priority Support", "Early Access to Events", "Exclusive Competitions"].map((feature) => (
                      <li key={feature} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-[var(--sunrise-orange)]" />
                        <span className="text-white/90">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={() => navigate("/register")}
                    className="w-full btn-accent"
                    data-testid="annual-plan-btn"
                  >
                    Start Annual
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partner Retailers */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[var(--deep-ocean)] mb-4">
              Partner Retailers
            </h2>
            <p className="text-[var(--river-stone)] text-lg">
              Enjoy 10% off at these leading tackle shops
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {retailers.map((retailer, index) => (
              <motion.div
                key={retailer.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-[var(--bg-main)] border-0 h-full">
                  <CardContent className="p-6 text-center">
                    <Store className="w-10 h-10 text-[var(--deep-ocean)] mx-auto mb-4" />
                    <h4 className="font-bold text-[var(--deep-ocean)] mb-1">{retailer.name}</h4>
                    <Badge className="bg-[var(--sunrise-orange)]/20 text-[var(--sunrise-orange)]">
                      {retailer.discount_percent}% OFF
                    </Badge>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Member of the Month */}
      {memberOfMonth?.member && (
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="gradient-ocean border-0 overflow-hidden">
                <CardContent className="p-8 md:p-12">
                  <div className="flex items-center gap-3 mb-6">
                    <Star className="w-8 h-8 text-[var(--sunrise-orange)]" />
                    <h2 className="text-2xl md:text-3xl font-bold text-white">Angler of the Month</h2>
                  </div>
                  <div className="flex flex-col md:flex-row gap-8 items-center">
                    <div className="w-32 h-32 rounded-full bg-white/20 flex items-center justify-center">
                      <span className="text-5xl font-bold text-white">
                        {memberOfMonth.member.name[0]}{memberOfMonth.member.surname[0]}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-2">
                        {memberOfMonth.member.name} {memberOfMonth.member.surname}
                      </h3>
                      <p className="text-white/70 mb-4">{memberOfMonth.member.area}</p>
                      <div className="flex flex-wrap gap-2">
                        {memberOfMonth.member.fishing_types?.slice(0, 3).map((type) => (
                          <Badge key={type} className="bg-white/20 text-white">{type}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[var(--deep-ocean)]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Join the Community?
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Start your FishingFanatix journey today and connect with anglers across South Africa.
            </p>
            <Button 
              onClick={() => navigate("/register")}
              className="btn-accent text-lg px-12 py-6"
              data-testid="cta-join-btn"
            >
              Become a Member <ChevronRight className="ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-[#00283D]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img src="/logo.jpg" alt="FishingFanatix" className="h-10 w-auto" />
            </div>
            <div className="flex items-center gap-6 text-white/60 text-sm">
              <a href="https://fishingfanatix.co.za" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">
                www.fishingfanatix.co.za
              </a>
              <span>|</span>
              <span>&copy; {new Date().getFullYear()} FishingFanatix. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
